import React from 'react';
import { X, FileText, Brain, Upload, CheckCircle, AlertCircle } from 'lucide-react';

interface HelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const HelpModal: React.FC<HelpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center space-x-2">
            <Brain className="h-6 w-6 text-primary-500" />
            <span>Hướng dẫn sử dụng PDF Reader</span>
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Overview */}
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-2">
              Tổng quan
            </h3>
            <p className="text-blue-800 dark:text-blue-300">
              PDF Reader là công cụ phân tích tài liệu PDF thông minh sử dụng AI. 
              Bạn có thể upload file PDF và nhận được phân tích chi tiết về nội dung.
            </p>
          </div>

          {/* How to use */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <Upload className="h-5 w-5 text-primary-500" />
              <span>Cách sử dụng</span>
            </h3>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  1
                </div>
                <div>
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Upload file PDF:</strong> Click vào vùng "Drop files here" hoặc click để chọn file
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  2
                </div>
                <div>
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Phân tích:</strong> Click nút "Analyze Content" để bắt đầu phân tích
                  </p>
                </div>
              </div>
              <div className="flex items-start space-x-3">
                <div className="flex-shrink-0 w-6 h-6 bg-primary-500 text-white rounded-full flex items-center justify-center text-sm font-medium">
                  3
                </div>
                <div>
                  <p className="text-gray-700 dark:text-gray-300">
                    <strong>Xem kết quả:</strong> Kết quả phân tích sẽ hiển thị ở bên phải
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* File requirements */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <FileText className="h-5 w-5 text-primary-500" />
              <span>Yêu cầu file</span>
            </h3>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700 dark:text-gray-300">Định dạng: PDF (.pdf)</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700 dark:text-gray-300">Dung lượng tối đa: 50MB</span>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span className="text-gray-700 dark:text-gray-300">File phải có nội dung text</span>
              </div>
            </div>
          </div>

          {/* Features */}
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
              <Brain className="h-5 w-5 text-primary-500" />
              <span>Tính năng phân tích</span>
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Tóm tắt 30 giây</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Tóm tắt ngắn gọn các điểm chính của tài liệu
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Thông số kỹ thuật</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Trích xuất các thông số, số liệu quan trọng
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">Cấu trúc tài liệu</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Phân tích cấu trúc và các phần chính
                </p>
              </div>
              <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3">
                <h4 className="font-medium text-gray-900 dark:text-white mb-1">So sánh & Nâng cấp</h4>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Tìm các thông tin so sánh trong tài liệu
                </p>
              </div>
            </div>
          </div>

          {/* Tips */}
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-4">
            <h3 className="text-lg font-semibold text-yellow-900 dark:text-yellow-200 mb-2 flex items-center space-x-2">
              <AlertCircle className="h-5 w-5" />
              <span>Mẹo sử dụng</span>
            </h3>
            <ul className="space-y-1 text-yellow-800 dark:text-yellow-300">
              <li>• File PDF có text rõ ràng sẽ cho kết quả tốt hơn</li>
              <li>• Tránh file PDF scan từ hình ảnh chất lượng thấp</li>
              <li>• Kết quả phân tích có thể copy để sử dụng</li>
              <li>• Thời gian xử lý phụ thuộc vào độ phức tạp của tài liệu</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end p-6 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="btn-primary px-6 py-2"
          >
            Đã hiểu
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
